import React, { useState } from "react";
import { EvtSectionHeader } from "components/header";
import { useParams } from "react-router-dom";
import { fetchRepoForGithub, removeEmployeeAccess, updateEmployeeRole } from "./api";
import { toast } from "sonner";
import { PermissionControlledInput } from "permissions/components/input";
import { Button } from "ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "ui/components/dropdown-menu";
import { Badge } from "ui/components/badge";
import { CustomList, Icon } from "components";
import { MENU } from "icons";
import { Avatar } from "ui/components/avatar";
import { RenderAvatar } from "components/render-avatar";
import RepoEmplopyeesInputSheet from "./input";
import { eventBus } from "utils/eventBus";
import { useQuery } from "@tanstack/react-query";
import { invalidateAllQueries } from "utils/utils";
import { GITHUB_ROLES, getRoleConfig } from "./constants";

// Project → Integrations → GitHub: repo header + the shared access manager.
export function IntegrationGithubRepo() {
  const { repo_id } = useParams();
  const { data: repoData, isLoading } = useQuery({
    queryKey: ["github-repo", repo_id],
    queryFn: () => fetchRepoForGithub({ id: repo_id }),
  });
  const repo = Array.isArray(repoData) ? repoData[0] : repoData;
  if (isLoading) {
    return (
      <div className="flex-1">
        <EvtSectionHeader title="Loading repository..." description="Fetching repository details" />
      </div>
    );
  }
  return (
    <div className="flex-1">
      <EvtSectionHeader
        title={repo?.suffix || "GitHub Repository"}
        description={repo?.repository_url || "No repository URL provided"}
        className="py-6 border-b-1"
      />
      <GithubRepoAccess repositoryUrl={repo?.repository_url} listKey={repo_id} />
    </div>
  );
}

// Who has access to a GitHub repository, with add employees, change role and remove access.
// Shared by the project GitHub integration and Integrations → GitHub → repository → People.
// listKey is the CustomList storageKey; the add/update/remove handlers refresh the list with it.
export function GithubRepoAccess({ repositoryUrl, listKey, title }) {
  const repo_id = listKey;
  const repo = { repository_url: repositoryUrl };
  const [updatingEmployeeId, setUpdatingEmployeeId] = useState(null);

  const handleUpdateRole = async (employeeId, newRole) => {
    if (!repo?.repository_url || !employeeId) return;
    try {
      setUpdatingEmployeeId(employeeId);
      const res = await updateEmployeeRole({
        repository_url: repo.repository_url,
        employeeId,
        permission: newRole,
      });
      toast.success(res?.message || "Role updated successfully! ✨");
      invalidateAllQueries();
      eventBus.emit("refresh:list", `github/repository/list_access${repo_id}`);
    } catch (error) {
      const errorMessage =
        error?.response?.errors?.[0]?.message ||
        error?.message ||
        "Failed to update employee role";
      toast.error(errorMessage);
    } finally {
      setUpdatingEmployeeId(null);
    }
  };

  const columns = [
    {
      id: "employee.id",
      header: "Name",
      accessorKey: "employee",
      cell: ({ getValue }) => {
        const user = getValue();
        if (!user) return null;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="size-7">
              <RenderAvatar user={user} />
            </Avatar>
            <span>
              {user.first_name} {user.last_name}
            </span>
          </div>
        );
      },
    },
    {
      id: "github_username",
      header: "GitHub Username",
      accessorKey: "username",
      cell: ({ getValue }) => getValue() || "-",
    },
    {
      id: "repository_role",
      header: "Role",
      accessorKey: "role",
      cell: ({ getValue }) => {
        const role = getValue();
        if (!role) return <span className="text-muted-foreground">-</span>;
        const config = getRoleConfig(role);
        return (
          <Badge variant="outline" className={config?.badgeClass}>
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "",
      accessorKey: "employee.id",
      cell: ({ row }) => {
        const employeeId = row.original.employee?.id;
        const currentRole = row.original.repository_role;
        const isUpdating = updatingEmployeeId === employeeId;

        return (
          <div className="flex items-center gap-2 justify-end opacity-0 group-hover:opacity-100 transition">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="outline" disabled={isUpdating}>
                  <Icon icon={MENU} className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <PermissionControlledInput
                  rule="update:PC,JC"
                  collection="jobs"
                >
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      Change Role
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {GITHUB_ROLES.map((roleOption) => {
                        const isCurrentRole =
                          roleOption.aliases.includes(String(currentRole || "").toLowerCase());
                        return (
                          <DropdownMenuItem
                            key={roleOption.value}
                            disabled={isCurrentRole || isUpdating}
                            onClick={() => handleUpdateRole(employeeId, roleOption.value)}
                            className="flex items-center justify-between gap-4"
                          >
                            <span className="font-medium">{roleOption.label}</span>
                            <span className="text-base text-muted-foreground font-mono">
                              ({roleOption.value})
                            </span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={async () => {
                      try {
                        const res = await removeEmployeeAccess({
                          repository_url: repo.repository_url,
                          employeeId,
                        });
                        toast.success(
                          res?.message || "Removed access successfully"
                        );
                        invalidateAllQueries();
                        eventBus.emit("refresh:list", `github/repository/list_access${repo_id}`);
                      } catch (error) {
                        const errorMessage =
                          error?.response?.errors?.[0]?.message ||
                          error?.message;
                        toast.error(errorMessage);
                      }
                    }}
                  >
                    Remove Access
                  </DropdownMenuItem>
                </PermissionControlledInput>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      sortable: false,
      meta: { align: "right" },
    },
  ];

  const defaultFilters = {
    repository_url:  repo?.repository_url,
  };

  if (!repositoryUrl) return null;
  return (
    <>
      <RepoEmplopyeesInputSheet repository_url={repositoryUrl} show={true} repo_id={repo_id} />
      <CustomList
        title={title}
        storageKey={repo_id}
        endPoint="github/repository/list_access"
        filters={defaultFilters}
        columns={columns}
        perPage={50}
        sortBy={"employee.first_name"}
        hidePagination={true}
        showSearch={false}
      />
    </>
  );
}
